import axios from 'axios';
import { setUser } from '../features/auth/authSlice';
import { store } from '../store';
import { API_BASE_URL } from '../config/env';
import { extractAccessToken } from './tokenHelpers';
import { saveAuthData } from './authStorage';
import { fetchMe } from '../services/userApi';
import {
  adminSession,
  getStaffExpiresAt,
  setStaffExpiresInSeconds,
} from './session';
import { clearUser } from '../features/auth/authSlice';
import { normalizeStaffUser } from './staffUser';

const REFRESH_WARNING_SECONDS = 300;
const AUTO_REFRESH_THRESHOLD_SECONDS = 2700;

function secondsUntilStaffExpiry() {
  const expiresAt = getStaffExpiresAt();
  if (!expiresAt) {
    return null;
  }
  return (expiresAt - Date.now()) / 1000;
}

export const shouldShowRefreshNotification = () => {
  const timeUntilExpiry = secondsUntilStaffExpiry();
  if (timeUntilExpiry === null) {
    return false;
  }
  return timeUntilExpiry > 0 && timeUntilExpiry <= REFRESH_WARNING_SECONDS;
};

async function persistRefreshedSession(responseData) {
  const newToken = extractAccessToken(responseData);

  if (newToken) {
    adminSession.setBearerToken(newToken);
  }

  if (responseData?.expires_in) {
    setStaffExpiresInSeconds(responseData.expires_in);
  }

  if (responseData?.user) {
    const userData = normalizeStaffUser(responseData.user);
    store.dispatch(setUser({ user: userData, token: adminSession.getBearerToken() }));
    saveAuthData(userData, { expiresIn: responseData.expires_in });
    return true;
  }

  try {
    const me = await fetchMe();
    const userData = normalizeStaffUser(me);
    store.dispatch(setUser({ user: userData, token: adminSession.getBearerToken() }));
    saveAuthData(userData, {
      expiresIn: responseData.expires_in,
      expiresAt: me.session?.expires_at,
    });
  } catch {
    adminSession.clear();
    store.dispatch(clearUser());
    return false;
  }

  return true;
}

export const refreshToken = async () => {
  try {
    const token = adminSession.getBearerToken();
    const headers = { Accept: 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
      headers,
      withCredentials: true,
    });

    return persistRefreshedSession(response.data);
  } catch {
    return false;
  }
};

let refreshCheckInterval = null;

export const startTokenExpirationCheck = (showRefreshDialog) => {
  if (refreshCheckInterval) {
    clearInterval(refreshCheckInterval);
  }

  refreshCheckInterval = setInterval(() => {
    if (shouldShowRefreshNotification()) {
      showRefreshDialog();
    }
  }, 60000);

  if (shouldShowRefreshNotification()) {
    showRefreshDialog();
  }
};

export const startAutoTokenRefresh = () => {
  if (refreshCheckInterval) {
    clearInterval(refreshCheckInterval);
  }

  refreshCheckInterval = setInterval(async () => {
    const timeUntilExpiry = secondsUntilStaffExpiry();
    if (timeUntilExpiry !== null && timeUntilExpiry < AUTO_REFRESH_THRESHOLD_SECONDS) {
      await refreshToken();
    }
  }, 30 * 60 * 1000);

  setTimeout(async () => {
    const timeUntilExpiry = secondsUntilStaffExpiry();
    if (timeUntilExpiry !== null && timeUntilExpiry < AUTO_REFRESH_THRESHOLD_SECONDS) {
      await refreshToken();
    }
  }, 60000);
};

export const stopTokenExpirationCheck = () => {
  if (refreshCheckInterval) {
    clearInterval(refreshCheckInterval);
    refreshCheckInterval = null;
  }
};
